import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ZONES = ['SE1', 'SE2', 'SE3', 'SE4'];

const PSR_LABELS = {
    B01: 'Biomassa',
    B04: 'Fossilgas',
    B12: 'Vattenkraft',
    B14: 'Kärnkraft',
    B16: 'Sol',
    B18: 'Vindkraft offshore',
    B19: 'Vindkraft onshore',
};

const PSR_COLORS = {
    B01: '#6b8e23',
    B04: '#8b7355',
    B12: '#1e90ff',
    B14: '#9400d3',
    B16: '#ffd700',
    B18: '#00ced1',
    B19: '#32cd32',
};

export default function ActualGenerationDashboard() {
    const [zone, setZone] = useState('SE3');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async (selectedZone) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`/api/actual-generation?zone=${selectedZone}&date=${new Date().toISOString()}`);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte hämta produktionsdata');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(zone);
    }, [zone]);

    const buildChartData = () => {
        if (!data?.generation?.length) return null;

        const labels = data.generation[0]?.quantity?.map((_, i) => `H${i + 1}`) || [];
        const datasets = data.generation.map(series => ({
            label: PSR_LABELS[series.psrType] || series.psrType,
            data: series.quantity.map(q => q.quantity),
            backgroundColor: PSR_COLORS[series.psrType] || '#888',
            borderRadius: 3,
            stack: 'generation',
        }));

        return { labels, datasets };
    };

    const getTotals = () => {
        if (!data?.generation?.length) return [];
        return data.generation.map(series => {
            const total = series.quantity.reduce((sum, q) => sum + q.quantity, 0);
            const avg = total / series.quantity.length;
            return {
                psrType: series.psrType,
                label: PSR_LABELS[series.psrType] || series.psrType,
                avg: Math.round(avg),
                color: PSR_COLORS[series.psrType] || '#888',
            };
        }).sort((a, b) => b.avg - a.avg);
    };

    const chartData = buildChartData();
    const totals = getTotals();

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold mb-2">Aktuell Elproduktion</h1>
                <p className="text-gray-400 mb-6">Realtidsdata för elproduktion per bränsleslag · ENTSO-E A75</p>

                {/* Zone Selector */}
                <div className="flex gap-2 mb-6">
                    {ZONES.map(z => (
                        <button
                            key={z}
                            onClick={() => setZone(z)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                zone === z
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                        >
                            {z}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="text-center py-20 text-gray-400">
                        <div className="animate-spin h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        Hämtar produktionsdata...
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-300">
                        {error}
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            {totals.slice(0, 4).map(t => (
                                <div key={t.psrType} className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                                        <span className="text-xs text-gray-400">{t.label}</span>
                                    </div>
                                    <p className="text-2xl font-bold">{t.avg.toLocaleString()}</p>
                                    <p className="text-xs text-gray-500">medel MW</p>
                                </div>
                            ))}
                        </div>

                        {/* Stacked Bar Chart */}
                        {chartData && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="text-lg font-semibold mb-4">Timvis produktion (MW)</h2>
                                <div className="h-72">
                                    <Bar
                                        data={chartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { labels: { color: '#9ca3af', boxWidth: 12 } },
                                                tooltip: { mode: 'index', callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y?.toLocaleString('sv-SE')} MW` } }
                                            },
                                            scales: {
                                                x: { stacked: true, ticks: { color: '#6b7280' }, grid: { color: 'rgba(255,255,255,0.05)' } },
                                                y: { stacked: true, ticks: { color: '#6b7280', callback: v => `${v.toLocaleString('sv-SE')} MW` }, grid: { color: 'rgba(255,255,255,0.05)' } }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Full Breakdown Table */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                            <h2 className="text-lg font-semibold mb-4">Produktionsmix</h2>
                            <div className="space-y-3">
                                {totals.map(t => {
                                    const totalAll = totals.reduce((s, x) => s + x.avg, 0);
                                    const pct = totalAll > 0 ? Math.round((t.avg / totalAll) * 100) : 0;
                                    return (
                                        <div key={t.psrType}>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="flex items-center gap-2">
                                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                                                    {t.label}
                                                </span>
                                                <span className="text-gray-400">{t.avg.toLocaleString()} MW · {pct}%</span>
                                            </div>
                                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full transition-all"
                                                    style={{ width: `${pct}%`, backgroundColor: t.color }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <p className="text-xs text-gray-600 mt-4">
                            Period: {data.periodStart?.slice(0, 8)} · Uppdaterad: {data.actualDate}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
